import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { NAV_ITEMS } from './Sidebar.js'
import { useFocusTrap } from '../lib/useFocusTrap.js'
import { Icon } from '../components/index.js'
import '../styles/palette.css'

export interface CommandPaletteProps {
  open: boolean
  onClose: () => void
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const panel = useRef<HTMLDivElement>(null)

  // Same contract as every other overlay in this app (Dialog.tsx): focus
  // moves in on open, Tab/Shift+Tab cycle within the panel instead of
  // escaping to the sidebar behind it, and focus returns to whatever
  // triggered ⌘K when the palette closes.
  useFocusTrap(open, panel)

  useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  const results = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return NAV_ITEMS
    return NAV_ITEMS.filter((item) => item.label.toLowerCase().includes(needle))
  }, [query])

  if (!open) return null

  return (
    <div className="dialog-backdrop lc-palette-backdrop" style={{ alignItems: 'start', paddingTop: '12vh' }} onClick={onClose}>
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="lc-palette"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="lc-palette-search">
          <Icon name="MagnifyingGlass" size={16} />
          <input
            aria-label="Search or jump to a screen"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search or jump to…"
            // Borderless by design, but not focus-ring-less: the ring moves to
            // the search row (`.lc-palette-search:focus-within` in palette.css)
            // instead of boxing the input. Never an inline `outline: 'none'` —
            // it would beat that rule the way it did on every editor block
            // (see EditableText.tsx).
            style={{
              flex: 1, minWidth: 0, border: 0, background: 'transparent', color: 'var(--color-text)',
              padding: 'var(--space-3) 0', fontFamily: 'inherit', fontSize: 15,
            }}
          />
          <kbd className="lc-kbd" aria-hidden>esc</kbd>
        </div>
        <div className="stack" style={{ padding: '0 var(--space-2) var(--space-2)', maxHeight: '60vh', overflow: 'auto' }}>
          {results.length === 0 ? (
            <p className="text-muted" style={{ padding: 'var(--space-4) var(--space-3) var(--space-2)', margin: 0, fontSize: 13 }}>
              Nothing matches “{query}”.
            </p>
          ) : (
            <div role="group" aria-labelledby="palette-group-screens" className="stack">
              <span id="palette-group-screens" className="lc-palette-group">Go to</span>
              {results.map((item) => (
                <button
                  key={item.to}
                  type="button"
                  className="lc-palette-row"
                  onClick={() => { navigate(item.to); onClose() }}
                >
                  <Icon name={item.icon} size={16} />
                  <span>{item.label}</span>
                  <span className="lc-palette-enter" aria-hidden>↵</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
